import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { format, parseISO, differenceInDays, isPast } from 'date-fns';
import { de } from 'date-fns/locale';
import { Machine } from '../../types/machine';
import { OperatingSupply, CreateOperatingSupplyRequest, UpdateOperatingSupplyRequest } from '../../types/inventory';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchOperatingSupplies,
  createOperatingSupply,
  updateOperatingSupply,
  deleteOperatingSupply,
} from '../../store/slices/inventorySlice';
import { StatusBadge } from '../inventory/StatusBadge';
import { LowStockIndicator, filterLowStockItems } from '../inventory/LowStockIndicator';
import { BulkActionsToolbar } from '../inventory/BulkActionsToolbar';

interface MachineOperatingSuppliesTabProps {
  machine: Machine;
}

const isExpiringSoon = (expiryDate: string | undefined, days: number = 30): boolean => {
  if (!expiryDate) return false;
  try {
    const expiry = parseISO(expiryDate);
    const daysUntilExpiry = differenceInDays(expiry, new Date());
    return daysUntilExpiry >= 0 && daysUntilExpiry <= days;
  } catch {
    return false;
  }
};

const isExpired = (expiryDate: string | undefined): boolean => {
  if (!expiryDate) return false;
  try {
    return isPast(parseISO(expiryDate));
  } catch {
    return false;
  }
};

export const MachineOperatingSuppliesTab: React.FC<MachineOperatingSuppliesTabProps> = ({ machine }) => {
  const dispatch = useAppDispatch();
  const { items: supplies, isLoading, isCreating } = useAppSelector((state) => state.inventory.operatingSupplies);
  const { user } = useAppSelector((state) => state.auth);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OperatingSupply | null>(null);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);

  const [formData, setFormData] = useState<CreateOperatingSupplyRequest>({
    name: '',
    description: '',
    quantity: 0,
    min_quantity: 0,
    unit: 'L',
    supplier: '',
    price: undefined,
    expiry_date: '',
  });

  const canEdit = user?.role === 'admin';
  const machineSupplies = supplies.filter(s => s.machine_id === machine.id);

  let displayedSupplies = machineSupplies;
  if (showLowStockOnly) {
    displayedSupplies = filterLowStockItems(displayedSupplies);
  }
  if (showExpiringOnly) {
    displayedSupplies = displayedSupplies.filter(s => isExpiringSoon(s.expiry_date, 30));
  }

  const expiringCount = machineSupplies.filter(s => isExpiringSoon(s.expiry_date, 30)).length;

  useEffect(() => {
    dispatch(fetchOperatingSupplies({ machineId: machine.id }));
  }, [dispatch, machine.id]);

  const handleOpenDialog = (item?: OperatingSupply) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        quantity: item.quantity,
        min_quantity: item.min_quantity,
        unit: item.unit,
        supplier: item.supplier || '',
        price: item.price,
        expiry_date: item.expiry_date ? format(parseISO(item.expiry_date), 'yyyy-MM-dd') : '',
      });
    } else {
      setSelectedItem(null);
      setFormData({
        name: '',
        description: '',
        quantity: 0,
        min_quantity: 0,
        unit: 'L',
        supplier: '',
        price: undefined,
        expiry_date: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
  };

  const handleSave = async () => {
    const dataToSend = {
      ...formData,
      expiry_date: formData.expiry_date || undefined,
    };

    if (selectedItem) {
      await dispatch(updateOperatingSupply({
        id: selectedItem.id,
        data: dataToSend as UpdateOperatingSupplyRequest,
      }));
    } else {
      await dispatch(createOperatingSupply({
        machineId: machine.id,
        data: dataToSend,
      }));
    }
    handleCloseDialog();
    dispatch(fetchOperatingSupplies({ machineId: machine.id }));
  };

  const handleDelete = async (id: string) => {
    await dispatch(deleteOperatingSupply(id));
    setDeleteDialogOpen(false);
    setSelectedItem(null);
    dispatch(fetchOperatingSupplies({ machineId: machine.id }));
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'quantity',
      headerName: 'Bestand',
      width: 150,
      renderCell: (params) => (
        <LowStockIndicator
          quantity={params.row.quantity}
          minQuantity={params.row.min_quantity}
          unit={params.row.unit}
          inline
        />
      ),
    },
    {
      field: 'unit',
      headerName: 'Einheit',
      width: 90,
    },
    {
      field: 'supplier',
      headerName: 'Lieferant',
      width: 150,
    },
    {
      field: 'expiry_date',
      headerName: 'Ablaufdatum',
      width: 150,
      renderCell: (params) => {
        if (!params.value) return '-';
        const expired = isExpired(params.value);
        const expiringSoon = isExpiringSoon(params.value, 30);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {(expired || expiringSoon) && <WarningIcon fontSize="small" color={expired ? 'error' : 'warning'} />}
            <Typography
              variant="body2"
              color={expired ? 'error' : expiringSoon ? 'warning.main' : 'text.primary'}
              sx={{ fontWeight: expired || expiringSoon ? 600 : 400 }}
            >
              {format(parseISO(params.value), 'dd.MM.yyyy', { locale: de })}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'price',
      headerName: 'Preis',
      width: 100,
      valueFormatter: (value: any) => value ? `€${value.toFixed(2)}` : '-',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Aktionen',
      width: 100,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Bearbeiten"
            onClick={() => handleOpenDialog(params.row as OperatingSupply)}
            disabled={!canEdit}
          />,
        ];
        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Löschen"
              onClick={() => {
                setSelectedItem(params.row as OperatingSupply);
                setDeleteDialogOpen(true);
              }}
            />
          );
        }
        return actions;
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Betriebsmittel ({displayedSupplies.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant={showLowStockOnly ? 'contained' : 'outlined'}
            size="small"
            onClick={() => {
              setShowLowStockOnly(!showLowStockOnly);
              if (!showLowStockOnly) setShowExpiringOnly(false);
            }}
          >
            Niedriger Bestand
          </Button>
          <Button
            variant={showExpiringOnly ? 'contained' : 'outlined'}
            size="small"
            onClick={() => {
              setShowExpiringOnly(!showExpiringOnly);
              if (!showExpiringOnly) setShowLowStockOnly(false);
            }}
          >
            Läuft ab
          </Button>
          <IconButton
            onClick={() => dispatch(fetchOperatingSupplies({ machineId: machine.id }))}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Hinzufügen
            </Button>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {filterLowStockItems(machineSupplies).length > 0 && !showLowStockOnly && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {filterLowStockItems(machineSupplies).length} Betriebsmittel mit niedrigem Bestand
        </Alert>
      )}
      {expiringCount > 0 && !showExpiringOnly && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {expiringCount} Betriebsmittel laufen in den nächsten 30 Tagen ab
        </Alert>
      )}

      {/* Bulk Actions */}
      {selectedRows.length > 0 && canEdit && (
        <BulkActionsToolbar
          selectedCount={selectedRows.length}
          onClear={() => setSelectedRows([])}
          inventoryType="operating-supply"
        />
      )}

      {/* Data Grid */}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={displayedSupplies}
          columns={columns}
          loading={isLoading}
          checkboxSelection={canEdit}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem ? 'Betriebsmittel bearbeiten' : 'Betriebsmittel hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Bestand"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Mindestbestand"
                type="number"
                value={formData.min_quantity}
                onChange={(e) => setFormData({ ...formData, min_quantity: Number(e.target.value) })}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Einheit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="L, kg, m³, etc."
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Lieferant"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Ablaufdatum"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Preis (€)"
                type="number"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value ? Number(e.target.value) : undefined })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || isCreating}
          >
            {selectedItem ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Betriebsmittel löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie "{selectedItem?.name}" wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={() => selectedItem && handleDelete(selectedItem.id)}
            variant="contained"
            color="error"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};